import tree from 'state';
import moment from 'moment';
import { find, get, isArray, deepClone, compact } from 'lodash';
import * as listingEffects from 'state/listings/effects';
import * as requestForQuoteEffects from 'state/requestForQuote/effects';
import { postUnAuthCriteria } from 'state/global/actions';

import asyncTreeRequester from 'utility/asyncTreeRequester';
import * as effects from './effects';

const productsCursor = tree.select(['products']);

function transformCriteria(sc) {
  const currentSc = deepClone(sc);
  currentSc.category = sc.category_id || sc.category; // latter comes from custom criteria
  currentSc.range = sc.range_id || sc.range; // latter comes from custom criteria

  if (
    (sc.metallurgy_group_id || sc.metallurgy_group) &&
    !isArray(sc.metallurgy_group)
  ) {
    currentSc.metallurgy_group = compact([sc.metallurgy_group_id]);
  }

  if ((sc.metallurgy_id || sc.metallurgy) && !isArray(sc.metallurgy)) {
    currentSc.metallurgy = compact([sc.metallurgy_id || sc.metallurgy]);
  }

  if ((sc.end_finish_id || sc.end_finish) && !isArray(sc.end_finish)) {
    currentSc.end_finish = compact([sc.end_finish_id || sc.end_finish]);
  }

  return currentSc;
}

export async function getProductDetails(id) {
  await asyncTreeRequester({
    func: effects.getProductDetails.bind(this, id),
    cursor: productsCursor,
    path: ['productDetails', id],
  });
}

export async function saveInquireProduct(
  { product, values, userId, cipher, token },
  previousAttemptedSaveData = {}
) {
  const id = 'q'; // arbitrary todo
  const user = get(values, 'user.value') || userId;
  const currentProduct = transformCriteria({
    ...product,
    quantity: values.quantity,
    quantity_uom: get(values, 'quantity_uom.id'),
    user,
  });
  let { rfqSubmission, itemSubmission } = previousAttemptedSaveData;
  let rfqTransition;

  if (!itemSubmission || get(itemSubmission, 'error')) {
    itemSubmission = await asyncTreeRequester({
      func: requestForQuoteEffects.saveItemData.bind(this, currentProduct, {
        cipher,
        token,
      }),
      cursor: productsCursor,
      path: ['productDetails', id, 'inquireProduct', 'rfq'],
    });
  }
  if (itemSubmission && itemSubmission.id && !get(rfqSubmission, 'id')) {
    const rfqData = {
      project_name: 'Product Inquiry',
      need_location_place_id: get(values, 'need_location.place_id'),
      need_date: /-\d*-/.test(values.need_date)
        ? values.need_date
        : moment(values.need_date).format('YYYY-MM-DD'),
      rfq_item: [itemSubmission.id],
      user,
    };
    rfqSubmission = await asyncTreeRequester({
      func: requestForQuoteEffects.createRfqGroup.bind(this, rfqData, {
        cipher,
        token,
      }),
      cursor: productsCursor,
      path: ['productDetails', id, 'inquireProduct', 'rfq'],
    });
  }

  if (get(rfqSubmission, 'id')) {
    rfqTransition = await asyncTreeRequester({
      func: requestForQuoteEffects.transitionRfq.bind(
        this,
        rfqSubmission.id,
        'not-quoted',
        undefined,
        { cipher, token }
      ),
      handleResult: true,
    });
  }

  return {
    rfqSubmission,
    itemSubmission,
    rfqTransition,
  };
}

export async function addProductToWishlist({
  listingId,
  productId,
  user = {},
  product,
  cipher,
  token,
}) {
  const id = productId || listingId;
  let sc = { ...product.criteria };
  if (user.user_id) {
    sc.user = user.user_id;
  }
  sc = transformCriteria(sc);

  try {
    const wishlistSubmission = await asyncTreeRequester({
      func: requestForQuoteEffects.saveUserInterestItems.bind(this, sc, {
        cipher,
        token,
      }),
      cursor: productsCursor,
      path: ['productDetails', id, 'wishlist', 'rfq'],
    });
    return wishlistSubmission;
  } catch (err) {
    return { error: err.message };
  }
}

export async function unAuthWishlist({
  product,
  path,
  publicMeta,
  hasCustomCriteria,
  listingId,
  productId,
  values,
}) {
  let criteriaResult;
  let criteriaResultId;
  if (hasCustomCriteria) {
    criteriaResult = await postUnAuthCriteria({ criteria: product });
    if (criteriaResult.error) {
      return { error: criteriaResult.error };
    }
    criteriaResultId = criteriaResult.id;
  }

  let inquiryType;
  if (hasCustomCriteria) {
    inquiryType = 'UnauthenticatedCriteria';
  } else if (listingId) {
    inquiryType = 'ListingPage';
  } else {
    inquiryType = 'ProductPage';
  }

  const contentType =
    publicMeta.unauthenticated_inquiry_content_types[inquiryType];
  const objectId = Number(criteriaResultId || listingId || productId);
  const action = get(
    find(publicMeta.unauthenticated_action, { name: 'Wish List' }),
    'id'
  );

  const data = {
    first_name: values.first_name,
    last_name: values.last_name,
    email: values.email,
    action,
    content_type: contentType,
    object_id: objectId,
    path: path || window.location.pathname,
  };

  try {
    await effects.unAuthInquire(data);
    return {
      checkEmail: true,
    };
  } catch (err) {
    return {
      error: err.message,
    };
  }
}

export async function unAuthInquire({
  product,
  publicMeta,
  hasCustomCriteria,
  path,
  listingId,
  productId,
  values,
}) {
  let criteriaResult;
  let criteriaResultId;
  if (hasCustomCriteria) {
    criteriaResult = await postUnAuthCriteria({ criteria: product });
    if (criteriaResult.error) {
      return { error: criteriaResult.error };
    }
    criteriaResultId = criteriaResult.id;
  }

  let inquiryType;
  if (hasCustomCriteria) {
    inquiryType = 'UnauthenticatedCriteria';
  } else if (listingId) {
    inquiryType = 'ListingPage';
  } else {
    inquiryType = 'ProductPage';
  }

  const contentType =
    publicMeta.unauthenticated_inquiry_content_types[inquiryType];
  const objectId = Number(criteriaResultId || listingId || productId);
  const action = get(
    find(publicMeta.unauthenticated_action, { name: 'Inquire' }),
    'id'
  );

  const data = {
    first_name: values.first_name,
    last_name: values.last_name,
    email: values.email,
    project_name: 'Product Inquiry',
    need_location_place_id: get(values, 'need_location.place_id'),
    need_date: moment(values.need_date).format('YYYY-MM-DD'),
    quantity: values.quantity,
    quantity_uom: get(values, 'quantity_uom.id'),
    action,
    content_type: contentType,
    object_id: objectId,
    path: path || window.location.pathname,
  };
  try {
    await effects.unAuthInquire(data);
    return {
      checkEmail: true,
    };
  } catch (err) {
    return {
      error: err.message,
    };
  }
}

export async function getRelatedListingsToProducts({
  pagination,
  filters,
  sorting,
  id,
  criteria,
} = {}) {
  await asyncTreeRequester({
    func: listingEffects.getSimilarListings.bind(this, { id, criteria }),
    pagination,
    filters,
    sorting,
    cursor: productsCursor,
    path: ['similarListings', id],
  });
}
